import { oauthClientAuthorization } from "./oauth-client-authorization.js";
import { getActiveServerBase } from "./oauth.js";
import { makeUrlAbsolute } from "@scalar/helpers/url/make-url-absolute";
import { replaceEnvVariables } from "@scalar/helpers/regex/replace-variables";
import { isObject } from "@scalar/helpers/object/is-object";
import { isAllowedOAuthUrl } from "@scalar/helpers/url/is-allowed-oauth-url";
import { redirectToProxy } from "@scalar/helpers/url/redirect-to-proxy";
//#region src/v2/blocks/scalar-auth-selector-block/helpers/oauth-device-authorization.ts
var REQUEST_TIMEOUT_MS = 3e4;
var MAX_TIMEOUT_MS = 2147483647;
/** Waits between polls, releasing the timer immediately when cancelled or expired. */
var waitForPoll = (milliseconds, signal) => new Promise((resolve, reject) => {
	signal.throwIfAborted();
	const abort = () => {
		clearTimeout(timer);
		reject(signal.reason);
	};
	const timer = setTimeout(() => {
		signal.removeEventListener("abort", abort);
		resolve();
	}, milliseconds);
	signal.addEventListener("abort", abort, { once: true });
});
var verificationUrl = (value) => {
	if (typeof value !== "string") throw new Error("Missing device verification URL");
	const url = new URL(value);
	if (!isAllowedOAuthUrl(url)) throw new Error("Device verification URL must use HTTPS or HTTP for local development URLs");
	return url.href;
};
/** Performs the RFC8628 device grant, respecting expiry, pending responses, and polling backoff. */
var authorizeDevice = async (flow, scopes, server, proxyUrl, environment, customFetch = fetch, options) => {
	try {
		if (!options) throw new Error("Device authorization requires a verification prompt");
		const resolveValue = (value) => replaceEnvVariables(value, environment);
		const endpoint = (value) => redirectToProxy(proxyUrl, makeUrlAbsolute(resolveValue(value), getActiveServerBase(server, environment)));
		const tokenUrl = flow["x-scalar-secret-token-url"] ?? flow.tokenUrl;
		if (!tokenUrl.trim()) throw new Error("Token URL is required");
		const clientId = resolveValue(flow["x-scalar-secret-client-id"]);
		const clientSecret = resolveValue(flow["x-scalar-secret-client-secret"]);
		const headers = { "Content-Type": "application/x-www-form-urlencoded" };
		const credentials = new URLSearchParams();
		if (clientSecret && flow["x-scalar-credentials-location"] !== "body") headers.Authorization = oauthClientAuthorization(clientId, clientSecret);
		else {
			credentials.set("client_id", clientId);
			if (clientSecret) credentials.set("client_secret", clientSecret);
		}
		const deviceBody = new URLSearchParams(credentials);
		if (scopes.length) deviceBody.set("scope", scopes.join(" "));
		const response = await customFetch(endpoint(flow.deviceAuthorizationUrl), {
			method: "POST",
			headers,
			body: deviceBody,
			signal: AbortSignal.any([options.signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)])
		});
		const data = await response.json();
		options.signal.throwIfAborted();
		if (!isObject(data)) throw new Error("Invalid device authorization response");
		if (!response.ok) throw new Error(String(data.error_description ?? data.error ?? "Device authorization failed"));
		if (typeof data.device_code !== "string" || !data.device_code || typeof data.user_code !== "string" || !data.user_code || typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in) || data.expires_in <= 0 || data.interval !== void 0 && (typeof data.interval !== "number" || !Number.isFinite(data.interval) || data.interval < 0)) throw new Error("Invalid device authorization response");
		const lifetime = Math.min(data.expires_in * 1e3, MAX_TIMEOUT_MS);
		const expiresAt = Date.now() + lifetime;
		const expiry = AbortSignal.timeout(lifetime);
		const signal = AbortSignal.any([options.signal, expiry]);
		options.onPrompt({
			userCode: data.user_code,
			verificationUri: verificationUrl(data.verification_uri),
			...data.verification_uri_complete ? { verificationUriComplete: verificationUrl(data.verification_uri_complete) } : {}
		});
		const body = new URLSearchParams(credentials);
		for (const [key, value] of Object.entries(flow["x-scalar-security-body"] ?? {})) body.set(key, resolveValue(value));
		body.set("grant_type", "urn:ietf:params:oauth:grant-type:device_code");
		body.set("device_code", data.device_code);
		let interval = typeof data.interval === "number" ? Math.max(1e3, data.interval * 1e3) : 5e3;
		try {
			while (Date.now() < expiresAt) {
				await waitForPoll(Math.min(interval, expiresAt - Date.now()), signal);
				signal.throwIfAborted();
				if (Date.now() >= expiresAt) throw new Error("Device authorization expired");
				const tokenResponse = await customFetch(endpoint(tokenUrl), {
					method: "POST",
					headers,
					body,
					signal: AbortSignal.any([signal, AbortSignal.timeout(REQUEST_TIMEOUT_MS)])
				}).catch((error) => {
					signal.throwIfAborted();
					if (error instanceof Error && error.name === "TimeoutError") return null;
					throw error;
				});
				if (!tokenResponse) {
					interval = Math.min(interval * 2, expiresAt - Date.now());
					continue;
				}
				const token = await tokenResponse.json();
				if (!isObject(token)) throw new Error("Invalid device token response");
				if (token.error === "authorization_pending") continue;
				if (token.error === "slow_down") {
					interval = Math.min(interval + 5e3, expiresAt - Date.now());
					continue;
				}
				const accessToken = token[flow["x-tokenName"] || "access_token"];
				if (tokenResponse.ok && typeof accessToken === "string" && accessToken) return [null, {
					accessToken,
					...typeof token.refresh_token === "string" ? { refreshToken: token.refresh_token } : {}
				}];
				throw new Error(String(token.error_description ?? token.error ?? "Failed to get an access token"));
			}
			throw new Error("Device authorization expired");
		} catch (error) {
			if (expiry.aborted && !options.signal.aborted) throw new Error("Device authorization expired");
			throw error;
		}
	} catch (error) {
		return [error instanceof Error ? error : new Error("Device authorization failed", { cause: error }), null];
	}
};
//#endregion
export { authorizeDevice };

//# sourceMappingURL=oauth-device-authorization.js.map